import dayjs from 'dayjs'
import { forIn } from 'lodash'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import AppLayout from '../components/AppLayout'
import fetchFooterData from '../components/footer/action'
import { DestinationLanding } from '../features/destination'
import { getPageData } from '../features/landing-page/actions/getPageData'
import fetchPreCostedResortData from '../features/maldives/pre-costed/actions/fetchPreCostedResortData'
import { getPackagesV1 } from '../features/packages/actions/getPackages'
import getPackageRequestObject from '../features/packages/getPackageRequestObject'
import { getTourPackages } from '../features/tour-packages/actions/getTourPackages'
import getPackagesForLandingPage from '../utils/actions/getPackagesForLandingPage'
import isMobile from '../utils/isMobile'
import get from '../utils/lodashUtils/get'
import isEmpty from '../utils/lodashUtils/isEmpty'
import { fetchFromStrapi } from '../utils/strapi-utils/api'
import getAllStrapiObject from '../utils/strapi-utils/getAllStrapiObject'
import getStrapiObject from '../utils/strapi-utils/getStrapiObject'
import refreshStrapi from '../utils/strapi-utils/refreshStrapi'

const VacationsListingPage = dynamic(() =>
	import('../features/vacations/listing-page'),
)
const MaldivesPrecostedPage = dynamic(() =>
	import('../features/maldives/pre-costed'),
)
const CustomError = dynamic(() => import('./_error'))
const _ENABLE_STRAPI = true
const _ENABLE_STRAPI_STATIC_FILES = false

function Packages({
	notFound,
	errorCode,
	footerData,
	packages,
	sbPackages,
	createdTime,
	staticPageData,
	maldives,
	slug,
}) {
	const customData = get(packages?.campaignDetails, 'customData')
	const router = useRouter()
	const parsedCustomData = useMemo(() => {
		let data = {}
		if (typeof customData === 'string' && !isEmpty(customData)) {
			data = JSON.parse(customData)
		}
		return data
	}, [customData])

	const banner = useMemo(
		() => parsedCustomData?.data?.banner,
		[parsedCustomData],
	)

	const showBannerImage = useMemo(() => {
		return !isEmpty(banner?.image)
	}, [banner])

	const isPaid = useMemo(
		() => get(packages, 'campaignDetails.paid'),
		[packages],
	)

	if (maldives?.isMle) {
		return (
			<AppLayout
				locale={packages?.locale}
				hideFooter={true}
				navbarProps={{
					isTransparent: true,
				}}
			>
				<MaldivesPrecostedPage
					data={maldives?.data}
					isMobile={isMobile()}
					slug={slug?.[0]}
					locale={maldives?.locale}
					seoData={maldives?.seoData}
				/>
			</AppLayout>
		)
	}

	if (errorCode || notFound) {
		return <CustomError statusCode={notFound ? 404 : errorCode} />
	}

	if (staticPageData?.staticData) {
		return (
			<AppLayout
				footerData={footerData}
				footerProps={{
					css: {
						paddingBottom: '72px',
						'@lg': {
							paddingBottom: 0,
						},
					},
					hideTrustSection: true,
					hideBrandSection: true,
				}}
			>
				<DestinationLanding {...staticPageData} />
			</AppLayout>
		)
	} else {
		return (
			<AppLayout
				locale={packages?.locale}
				footerData={footerData}
				navbarProps={{
					isTransparent: true,
				}}
				footerProps={{
					css: {
						paddingBottom: '72px',
						'@lg': {
							paddingBottom: 0,
						},
					},
					hideTrustSection: true,
					hideBrandSection: true,
				}}
				isPaid={isPaid}
			>
				<VacationsListingPage
					data={packages}
					sbPackages={sbPackages}
					parsedCustomData={parsedCustomData}
					isMobileDevice={isMobile()}
					createdTime={createdTime}
					showBannerImage={showBannerImage}
					slug={slug}
				/>
			</AppLayout>
		)
	}
}

export default Packages

export async function getStaticProps({ params, locale }) {
	let footerData = [],
		pkgs = {},
		sbPackages = {},
		errorCode = false,
		mleResorts = {},
		isMle = false
	const { slug } = params
	let strapiValue = {}

	if (_ENABLE_STRAPI) {
		try {
			await getStrapiObject({ slug: slug?.join('/') })
				.then(data => (strapiValue = data))
				.catch(error => {
					console.log(JSON.stringify(error))
				})
		} catch (error) {
			console.log(JSON.stringify(error))
		}
	}
	const collection = strapiValue
		? strapiValue?.collection?.endsWith('s')
			? strapiValue?.collection
			: strapiValue?.collection + 's'
		: null
	if (
		strapiValue?.collection &&
		collection !== 'speed-boat-flows' &&
		collection !== null
	) {
		// const collection = strapiValue?.collection?.endsWith('s')
		// 	? strapiValue?.collection
		// 	: strapiValue?.collection + 's'
		let strapiResponse = null
		try {
			strapiResponse = await fetchFromStrapi(
				`/${collection}/${strapiValue.id}`,
				{
					populate: [
						'bannerTitle',
						'pageDescription',
						'pageDescription.image',
						'bannerMedia.bannerImage',
						'pageContents',
						'pageContents.data',
						'pageContents.data.image',
						'pageContents.image',
						'pageContent.card-listing',
						//seo&&og
						'seo.metaImage',
						'seo.metaSocial.title',
						'seo.metaSocial.image',
						'seo.metaSocial.socialNetwork',
						'seo.metaSocial.description',
						// Breadcrumb data
						'things_to_do.bannerTitle',
						'places_to_visit.bannerTitle',
						'city.bannerTitle',
						'city.country.bannerTitle',
						'city.country.continent.bannerTitle',
						'country.bannerTitle',
						'country.continent.bannerTitle',
						'region.bannerTitle',
						'region.continent.bannerTitle',
						'continent.bannerTitle',
						// Relational contents
						'countries.bannerTitle',
						'countries.bannerMedia.bannerImage',
						'regions.bannerTitle',
						'regions.bannerMedia.bannerImage',
						'cities.bannerTitle',
						'cities.bannerMedia.bannerImage',
						'activities.bannerTitle',
						'activities.bannerMedia.bannerImage',
						'attractions.bannerTitle',
						'attractions.bannerMedia.bannerImage',
						'things_to_dos.bannerTitle',
						'things_to_dos.bannerMedia.bannerImage',
						'places_to_visits.bannerTitle',
						'places_to_visits.bannerMedia.bannerImage',
						'best_time_to_visits.bannerTitle',
						'best_time_to_visits.bannerMedia.bannerImage',
						'how_to_reaches.bannerTitle',
						'how_to_reaches.bannerMedia.bannerImage',
						'guides.bannerTitle',
						'guides.bannerMedia.bannerImage',
						'visa_guidelines.bannerTitle',
						'visa_guidelines.bannerMedia.bannerImage',
						'journeys',
						'journeys.pageDescription',
						'journeys.bannerImage.bannerImage',
						'journeys.dayWisePlan.days.slots',
						'journeys.transfers',
					],
				},
			)
		} catch (error) {
			console.log('Error while fetching from Strapi: ', error)
		}

		// Basic list of product APIs for the page
		const productApis = [fetchFooterData({ locale })]

		const { productListingPageSlug, pageContents } =
			strapiResponse?.data?.attributes || {}

		// Get product packages listing page if key => productListingPageSlug has the value
		if ((productListingPageSlug || '').trim().length) {
			const reqObj = getPackageRequestObject(
				{},
				(productListingPageSlug || '').trim(),
				{
					testimonials: 0,
					limit: 10,
				},
			)

			productApis.push(getPackagesV1(reqObj))
		}

		// Interlinking based on tags from dynamic records
		const productInterlinkingTagsList = []
		pageContents?.forEach(item => {
			// Product interlinking query
			if (!item?.data?.length && item.productInterlinkingTags) {
				productInterlinkingTagsList.push(item.productInterlinkingTags)
			}
		})

		// Check if there are any component with interlinkingTags then add API to get data
		if (productInterlinkingTagsList.length) {
			const requestObj = {
				limit: 12,
				tags: productInterlinkingTagsList,
			}

			productApis.push(getPackagesForLandingPage(requestObj))
		}

		const [
			footerData,
			productPackagesListingData = null,
			productInterlinkingData = null,
		] = await Promise.all(productApis)

		return {
			props: {
				createdTime: new Date().toISOString(),
				errorCode: errorCode ? errorCode : null,
				footerData,
				staticPageData: {
					staticData: strapiResponse?.data?.attributes || null,
					productPackagesListingData,
					productInterlinkingData,
				},
			},
			revalidate: 120, // 2 minutes
		}
	} else {
		if (!slug?.includes('undefined')) {
			if (slug?.[0]?.includes('maldives-resort')) {
				try {
					await fetchPreCostedResortData(slug?.[0], locale)
						.then(data => {
							isMle = true
							if (data?.message == 'Not found') {
								errorCode = 404
							} else {
								mleResorts = data
							}
						})
						.catch(error => {
							errorCode = error?.status
						})
					const strapiResponse = await fetchFromStrapi(`/speed-boat-flows`, {
						filters: {
							slug: slug[0],
						},
						// populate: 'deep,5',
						populate: [
							'bannerTitle',
							'pageDescription',
							'pageDescription.image',
							'bannerMedia.bannerImage',
							'pageContents',
							'pageContents.data',
							'pageContents.data.image',
							'pageContents.image',
							'pageContent.card-listing',
							//seo&&og
							'seo.metaImage',
							'seo.metaSocial.title',
							'seo.metaSocial.image',
							'seo.metaSocial.socialNetwork',
							'seo.metaSocial.description',
							// Breadcrumb data
							'things_to_do.bannerTitle',
							'places_to_visit.bannerTitle',
							'city.bannerTitle',
							'city.country.bannerTitle',
							'city.country.continent.bannerTitle',
							'country.bannerTitle',
							'country.continent.bannerTitle',
							'region.bannerTitle',
							'region.continent.bannerTitle',
							'continent.bannerTitle',
							// Relational contents
							'countries.bannerTitle',
							'countries.bannerMedia.bannerImage',
							'regions.bannerTitle',
							'regions.bannerMedia.bannerImage',
							'cities.bannerTitle',
							'cities.bannerMedia.bannerImage',
							'activities.bannerTitle',
							'activities.bannerMedia.bannerImage',
							'attractions.bannerTitle',
							'attractions.bannerMedia.bannerImage',
							'things_to_dos.bannerTitle',
							'things_to_dos.bannerMedia.bannerImage',
							'places_to_visits.bannerTitle',
							'places_to_visits.bannerMedia.bannerImage',
							'best_time_to_visits.bannerTitle',
							'best_time_to_visits.bannerMedia.bannerImage',
							'how_to_reaches.bannerTitle',
							'how_to_reaches.bannerMedia.bannerImage',
							'guides.bannerTitle',
							'guides.bannerMedia.bannerImage',
							'visa_guidelines.bannerTitle',
							'visa_guidelines.bannerMedia.bannerImage',
							'journeys',
							'journeys.pageDescription',
							'journeys.bannerImage.bannerImage',
							'journeys.dayWisePlan.days.slots',
							'journeys.transfers',
						],
					})
					// console.log({ strapiResponse: strapiResponse?.data[0]?.attributes })
					if (errorCode === 404) {
						return {
							props: {
								errorCode: 404,
							},
						}
					} else if (errorCode == 400) {
						return {
							props: {
								createdTime: new Date().toISOString(),
								errorCode: errorCode ? errorCode : null,
								footerData,
								packages: {
									...pkgs,
									locale,
								},
								sbPackages,
								staticPageData: null,
								slug,
							},
							revalidate: 120, // 2 minutes
						}
					} else {
						return {
							props: {
								createdTime: new Date().toISOString(),
								errorCode: errorCode ? errorCode : null,
								footerData,
								packages: {
									...pkgs,
									locale,
								},
								maldives: {
									data: mleResorts,
									isMle,
									locale,
									seoData: strapiResponse?.data?.length
										? strapiResponse?.data[0]?.attributes
										: null,
								},
								staticPageData: null,
								slug,
							},
							revalidate: 100,
						}
					}
				} catch (error) {
					return {
						props: {
							createdTime: new Date().toISOString(),
							errorCode: errorCode ? errorCode : null,
							footerData,
							packages: {
								...pkgs,
								locale,
							},
							sbPackages,
							staticPageData: null,
							slug,
						},
						revalidate: 120, // 2 minutes
					}
				}
			} else {
				const key = slug.join('/')
				const showSbCards = false
				// const showSbCards =
				// 	['honeymoon-packages/maldives', 'packages/maldives'].includes(key) &&
				// 	locale === 'in'

				if (showSbCards) {
					const packageReqObj = getPackageRequestObject({}, key, {})

					const reqObj = {
						departureAirportCode: '$$$',
						arrivalAirportCode: '$$$',
						departureDate: dayjs().add(45, 'day').format('YYYY-MM-DD'),
						passengerConfiguration: [{ adultCount: 2, childAges: [] }],
						nights: 4,
						nationality: 'IN',
						onwardArrivalAirportCode: 'MLE',
						returnDepartureAirportCode: 'MLE',
						topHotelsOnly: true,
					}

					await Promise.all([
						fetchFooterData({ locale }),
						getPackagesV1(packageReqObj, locale),
						getTourPackages(reqObj, locale),
					])
						.then(values => {
							footerData = values[0]
							pkgs = values[1]
							sbPackages = values[2]
						})
						.catch(err => {
							errorCode = err.status
						})
				} else {
					const reqObj = getPackageRequestObject({}, key, {})
					await Promise.all([
						fetchFooterData({ locale }),
						getPackagesV1(reqObj, locale),
					])
						.then(values => {
							footerData = values[0]
							pkgs = values[1]
						})
						.catch(err => {
							errorCode = err.status
						})
				}

				return {
					props: {
						createdTime: new Date().toISOString(),
						errorCode: errorCode ? errorCode : null,
						footerData,
						packages: {
							...pkgs,
							locale,
						},
						sbPackages,
						staticPageData: null,
						slug: key,
					},
					revalidate: 120, // 2 minutes
				}
			}
		}
	}
}

export async function getStaticPaths({ locales }) {
	let paths = [],
		pathsFromCms = {},
		pathsFromStrapi = {}

	// Internal CMS
	const reqObj = {
		pageName: 'pre_render_pages',
		locale: 'in',
	}

	try {
		pathsFromCms = await getPageData(reqObj)
	} catch (error) {
		console.log(error)
	}

	for (const locale of locales) {
		const localeSlugs = pathsFromCms?.[locale] || []

		const localeBasedPaths = (localeSlugs || []).map(page => ({
			params: {
				slug: page.split('/'),
			},
			locale,
		}))

		paths = [...paths, ...localeBasedPaths]
	}

	if (_ENABLE_STRAPI && _ENABLE_STRAPI_STATIC_FILES) {
		await refreshStrapi().then(res => console.log(res))
		//Strapi CMS
		pathsFromStrapi = await getAllStrapiObject()
		const strapiArr = []
		forIn(pathsFromStrapi, value => {
			const data = JSON.parse(value)
			strapiArr.push({
				params: {
					slug: data?.slug?.split('/'),
				},
				locale: 'in',
			})
		})

		paths = [...paths, ...strapiArr]
	}

	return {
		paths, // Returning empty to avoid ISR style breaks
		fallback: 'blocking',
	}
}
